/**
 * AI PPT / Slide Generator — standalone gap tool.
 * POST /api/gap/ppt/generate → editable slide deck preview + copy/export buttons.
 */
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Presentation, Loader2, Copy, ArrowLeft, Sparkles, Download, Palette, Check } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import AIProgressLoader from "@/components/premium/AIProgressLoader";
import "../workflows/workflows.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function PPTGeneratorPage() {
  const navigate = useNavigate();
  const [topic, setTopic] = useState("");
  const [audience, setAudience] = useState("");
  const [duration, setDuration] = useState(15);
  const [slideCount, setSlideCount] = useState(8);
  const [tone, setTone] = useState("Professional");
  const [loading, setLoading] = useState(false);
  const [deck, setDeck] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState("");
  const [rendering, setRendering] = useState(false);
  const [themes, setThemes] = useState([]);
  const [themeId, setThemeId] = useState("modern_purple");
  const [customAccent, setCustomAccent] = useState("#A855F7");
  const [customBg, setCustomBg] = useState("#FFFFFF");

  // Load themes list
  useEffect(() => {
    axios
      .get(`${API}/api/gap/ppt/themes`)
      .then((r) => setThemes(r.data?.themes || []))
      .catch(() => setThemes([]));
  }, []);

  const currentTheme = themes.find((t) => t.id === themeId) || themes[0];

  const buildThemePayload = () => {
    if (themeId === "custom") {
      return {
        theme: "custom",
        custom_colors: {
          bg: customBg,
          alt_bg: customBg,
          accent: customAccent,
          title_text: "#111111",
          body_text: "#334155",
          muted: "#64748B",
          closing_bg: customAccent,
          closing_text: "#FFFFFF",
        },
      };
    }
    return { theme: themeId };
  };

  const submit = async (e) => {
    e.preventDefault();
    if (loading || !topic.trim()) return;
    setLoading(true);
    setDeck(null);
    setDownloadUrl("");
    try {
      const res = await axios.post(`${API}/api/gap/ppt/generate-and-render`, {
        topic, audience, duration_minutes: Number(duration), slide_count: Number(slideCount), tone,
        ...buildThemePayload(),
      });
      setDeck(res.data);
      if (res.data?.download_url) {
        setDownloadUrl(`${API}${res.data.download_url}`);
      }
      toast.success(`${res.data.slide_count} slides ready — download .pptx now!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const reRender = async () => {
    if (!deck?.slides || rendering) return;
    setRendering(true);
    try {
      const r = await axios.post(`${API}/api/gap/ppt/render`, {
        title: deck.topic || topic,
        subtitle: deck.audience || audience,
        slides: deck.slides,
        ...buildThemePayload(),
      });
      setDownloadUrl(`${API}${r.data.download_url}`);
      toast.success("PPT re-rendered with new theme");
    } catch (err) {
      toast.error("Re-render failed");
    } finally {
      setRendering(false);
    }
  };

  const copyDeck = () => {
    if (!deck) return;
    const text = (deck.slides || []).map((s, i) =>
      `Slide ${i+1}: ${s.title}\n${(s.bullets || []).map((b) => `  • ${b}`).join("\n")}\n  Notes: ${s.notes || ""}`
    ).join("\n\n");
    navigator.clipboard.writeText(text);
    toast.success("Deck copied as text");
  };

  return (
    <>
      <Header />
      <div className="wf-hub" data-testid="ppt-generator-page">
        <div className="wf-hub-inner" style={{ maxWidth: 880 }}>
          <button className="wf-btn wf-btn--ghost" onClick={() => navigate("/tools")} data-testid="ppt-back" style={{ marginBottom: 24 }}>
            <ArrowLeft size={14}/> All tools
          </button>
          <span className="wf-eyebrow">AI Slide Generator</span>
          <h1 className="wf-hub-title" data-testid="ppt-title">Topic in. <em>Editable deck out.</em></h1>
          <p className="wf-hub-sub" style={{ marginBottom: 36 }}>
            Tell us the topic, audience, and duration. We&rsquo;ll build a full slide deck with bullets and speaker notes.
            Used inside the <button onClick={() => navigate("/workflows/education")} style={{ background: "none", border: "none", color: "#6d28d9", textDecoration: "underline", cursor: "pointer", padding: 0 }}>Education workflow</button>.
          </p>

          <form onSubmit={submit} data-testid="ppt-form">
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: "0.95rem" }}>Topic *</label>
              <textarea value={topic} onChange={(e) => setTopic(e.target.value)} rows={2} required
                placeholder="e.g. How to use AI tools to grow a SaaS business in 2026"
                data-testid="ppt-topic"
                style={{ width: "100%", padding: "12px 14px", borderRadius: 10, border: "1px solid rgba(109,40,217,0.18)", fontSize: "0.95rem", background: "white", fontFamily: "inherit" }}/>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 18 }}>
              <Field label="Audience" value={audience} onChange={setAudience} placeholder="e.g. founders" testid="ppt-audience"/>
              <Field label="Duration (mins)" value={duration} onChange={setDuration} type="number" testid="ppt-duration"/>
              <Field label="Slide count" value={slideCount} onChange={setSlideCount} type="number" testid="ppt-slides"/>
              <SelectField label="Tone" value={tone} onChange={setTone} options={["Professional", "Casual", "Educational", "Inspiring"]} testid="ppt-tone"/>
            </div>

            {/* THEME PICKER */}
            <div style={{ marginBottom: 22 }} data-testid="ppt-theme-picker">
              <label style={{ display: "block", fontWeight: 600, marginBottom: 8, fontSize: "0.95rem" }}>
                <Palette size={14} style={{ display: "inline", marginRight: 6, verticalAlign: "-2px", color: "#A855F7" }}/>
                Choose a theme
              </label>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10 }}>
                {themes.map((t) => {
                  const active = themeId === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setThemeId(t.id)}
                      data-testid={`ppt-theme-${t.id}`}
                      style={{
                        position: "relative",
                        padding: 10, borderRadius: 14, cursor: "pointer", textAlign: "left",
                        border: active ? "2px solid #A855F7" : "2px solid #E2E8F0",
                        background: "white",
                        boxShadow: active ? "0 0 0 4px rgba(168,85,247,0.14)" : "none",
                        transition: "all 0.15s",
                      }}
                    >
                      <div style={{
                        height: 42, borderRadius: 8, marginBottom: 6,
                        background: t.preview_bg,
                        border: t.preview_bg === "#FFFFFF" ? "1px solid #E2E8F0" : "none",
                        position: "relative", overflow: "hidden",
                      }}>
                        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: t.preview_accent }}/>
                        <div style={{ padding: "10px 8px 0", fontSize: 9, fontWeight: 700, color: t.preview_text || "#111" }}>
                          Aa
                        </div>
                      </div>
                      <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#111", lineHeight: 1.3 }}>{t.label}</div>
                      {active && <Check size={13} style={{ position: "absolute", top: 6, right: 6, color: "#A855F7" }}/>}
                    </button>
                  );
                })}
                {/* Custom */}
                <button
                  type="button"
                  onClick={() => setThemeId("custom")}
                  data-testid="ppt-theme-custom"
                  style={{
                    position: "relative",
                    padding: 10, borderRadius: 14, cursor: "pointer", textAlign: "left",
                    border: themeId === "custom" ? "2px solid #A855F7" : "2px dashed #CBD5E1",
                    background: "#FAFAFA",
                    boxShadow: themeId === "custom" ? "0 0 0 4px rgba(168,85,247,0.14)" : "none",
                  }}
                >
                  <div style={{
                    height: 42, borderRadius: 8, marginBottom: 6,
                    background: `linear-gradient(135deg, ${customBg} 0%, ${customAccent} 100%)`,
                    position: "relative",
                  }}>
                    <div style={{ padding: "10px 8px 0", fontSize: 9, fontWeight: 700, color: "#111" }}>
                      Aa
                    </div>
                  </div>
                  <div style={{ fontSize: "0.72rem", fontWeight: 600, color: "#111" }}>Custom</div>
                  {themeId === "custom" && <Check size={13} style={{ position: "absolute", top: 6, right: 6, color: "#A855F7" }}/>}
                </button>
              </div>

              {themeId === "custom" && (
                <div style={{ display: "flex", gap: 12, marginTop: 12, flexWrap: "wrap" }} data-testid="ppt-custom-colors">
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Accent</label>
                    <input type="color" value={customAccent} onChange={(e) => setCustomAccent(e.target.value)}
                      style={{ width: 36, height: 36, border: "1px solid #E2E8F0", borderRadius: 8, cursor: "pointer" }}
                      data-testid="ppt-custom-accent"/>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Background</label>
                    <input type="color" value={customBg} onChange={(e) => setCustomBg(e.target.value)}
                      style={{ width: 36, height: 36, border: "1px solid #E2E8F0", borderRadius: 8, cursor: "pointer" }}
                      data-testid="ppt-custom-bg"/>
                  </div>
                </div>
              )}
            </div>

            <button type="submit" className="wf-btn" disabled={loading} data-testid="ppt-submit">
              {loading ? <><Loader2 size={16} className="animate-spin"/> Generating…</> : <><Sparkles size={16}/> Generate deck</>}
            </button>
          </form>

          {loading && (
            <div style={{ marginTop: 28 }} data-testid="ppt-loading">
              <AIProgressLoader
                visible={loading}
                label="building your slide deck"
                stages={[
                  "outlining sections and learning beats",
                  "writing slide bullets and speaker notes",
                  "rendering the .pptx file",
                  "finalising download",
                ]}
              />
            </div>
          )}

          {deck && (
            <div style={{ marginTop: 36 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, flexWrap: "wrap", gap: 10 }}>
                <h2 style={{ fontSize: "1.3rem", fontWeight: 700, margin: 0 }} data-testid="ppt-deck-title">
                  <Presentation size={20} style={{ display: "inline", marginRight: 8, verticalAlign: "-3px", color: "#6d28d9" }}/>
                  {deck.slide_count} slides
                </h2>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {downloadUrl ? (
                    <>
                      <a href={downloadUrl} download className="wf-btn" data-testid="ppt-download" style={{ background: "#A855F7", color: "white", textDecoration: "none" }}>
                        <Download size={14}/> Download .pptx
                      </a>
                      <button className="wf-btn wf-btn--ghost" onClick={reRender} disabled={rendering} data-testid="ppt-rerender">
                        {rendering ? <><Loader2 size={14} className="animate-spin"/> Re-rendering…</> : <><Palette size={14}/> Change theme</>}
                      </button>
                    </>
                  ) : (
                    <button className="wf-btn" onClick={reRender} disabled={rendering} data-testid="ppt-render">
                      {rendering ? <><Loader2 size={14} className="animate-spin"/> Rendering…</> : <><Download size={14}/> Render .pptx</>}
                    </button>
                  )}
                  <button className="wf-btn wf-btn--ghost" onClick={copyDeck} data-testid="ppt-copy"><Copy size={14}/> Copy text</button>
                </div>
              </div>
              {(deck.slides || []).map((s, i) => (
                <div key={i} className="wf-asset" data-testid={`ppt-slide-${i}`}>
                  <div style={{ fontSize: "0.7rem", color: "#6d28d9", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>Slide {i+1}</div>
                  <h3 style={{ fontSize: "1.15rem", margin: "0 0 10px", letterSpacing: "-0.01em" }}>{s.title}</h3>
                  <ul style={{ paddingLeft: 18, lineHeight: 1.65, margin: "0 0 10px" }}>{(s.bullets || []).map((b, j) => <li key={j}>{b}</li>)}</ul>
                  {s.notes && <p style={{ fontSize: "0.85rem", color: "#5a4d6f", margin: 0, paddingTop: 8, borderTop: "1px dashed rgba(109,40,217,0.12)" }}><strong>Notes:</strong> {s.notes}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
  return (
    <div>
      <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: "0.9rem" }}>{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
        style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(109,40,217,0.18)", fontSize: "0.92rem", background: "white" }}/>
    </div>
  );
}
function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label style={{ display: "block", fontWeight: 600, marginBottom: 6, fontSize: "0.9rem" }}>{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        style={{ width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(109,40,217,0.18)", fontSize: "0.92rem", background: "white" }}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
